<script setup lang="ts">
import { useModalStore } from '@/shared/ui/AppModal/store/modal';
import EmblaCarousel from '@/shared/ui/EmblaCarousel/index';
import type { EmblaCarouselType } from 'embla-carousel';
import type { ILawyerCardGalleryProps } from '@/widgets/LawyerCard/model/types/lawyerCard';

const props = withDefaults(defineProps<ILawyerCardGalleryProps>(), {
  gallery: () => [],
});

const modalStore = useModalStore();

const isMobile: boolean = useIsMobile();
const isPrevActive: Ref<boolean> = ref(false);
const isNextActive: Ref<boolean> = ref(false);
const emblaApi: Ref<EmblaCarouselType | null> = ref(null);

const updateButtonsState = (): void => {
  const embla = emblaApi.value;
  if (!embla) return;
  isPrevActive.value = embla.canScrollPrev();
  isNextActive.value = embla.canScrollNext();
};

const onInitEmbla = (embla: EmblaCarouselType): void => {
  emblaApi.value = embla;
  embla.on('select', updateButtonsState);
  embla.on('reInit', updateButtonsState);
  embla.on('resize', updateButtonsState);
  updateButtonsState();
};

const showModal = (imageUrl: string, index: number): void => {
  modalStore.setModalState('image', true, {
    url: imageUrl,
    gallery: props.gallery,
    alt: 'Галерея',
    isSlider: true,
    startIndex: index,
  });
};
</script>

<template>
  <div :class="['lawyer-card-gallery', { _mobile: isMobile }]">
    <div class="lawyer-card-gallery__title">Галерея</div>
    <EmblaCarousel
      :class="[
        'lawyer-card-gallery__slider',
        { '_shadow-left': isPrevActive, '_shadow-right': isNextActive },
      ]"
      :options="{
        slidesToScroll: 1,
        align: 'start',
        containScroll: 'trimSnaps',
        skipSnaps: false,
      }"
      @on-init="onInitEmbla"
    >
      <div
        v-for="(item, index) in gallery"
        :key="index"
        class="lawyer-card-gallery__item"
      >
        <NuxtImg
          class="lawyer-card-gallery__image"
          :src="item"
          alt="Фото из галереи юриста"
          width="60"
          height="60"
          densities="x1 x2"
          loading="lazy"
          @click.prevent="showModal(item, index)"
        />
      </div>
    </EmblaCarousel>
  </div>
</template>

<style lang="less">
.lawyer-card-gallery {
  .flex-column(@size-x3);

  border-top: 1px solid @color-alto;
  padding-top: @size-x5;

  &__title {
    font-size: @size-x4;
    font-weight: 700;
    line-height: 130%;
  }

  &__image {
    display: block;
    width: 60px;
    height: 60px;
    object-fit: cover;
    cursor: zoom-in;
    border-radius: @main-size;
  }

  &__slider {
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
      box-shadow: -@size-x5 @size-x7 @size-x2 @size-x10 #fff;
      z-index: 1;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &._shadow-left::before {
      opacity: 1;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 0;
      height: 0;
      box-shadow: @size-x5 @size-x7 @size-x2 @size-x10 #fff;
      z-index: 1;
      opacity: 1;
      transition: opacity 0.3s ease;
    }

    &:not(._shadow-right)::after {
      opacity: 0;
    }

    .embla__container {
      gap: @size-x3;
    }

    .slider-arrow:disabled {
      pointer-events: none;
    }
  }

  &._mobile &__title {
    font-size: 14px;
  }

  &._mobile &__image {
    width: 45px;
    height: 45px;
  }

  &._mobile &__slider {
    .embla__container {
      gap: @size-x2;
    }
  }
}
</style>
